import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Avatar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import { appendTicketLog } from '../lib/ticketLogs';

function optionalString(v) {
  const t = (v || '').trim();
  return t.length ? t : null;
}

/**
 * Edit another user’s directory profile (Firestore `users/{targetUid}` + avatar storage).
 * Login email is read-only from the stored `email` field.
 */
export default function TeamMemberProfileEditor({ targetUid, onSaved, auditLog }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [storedEmail, setStoredEmail] = useState('');
  const [role, setRole] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  const [fullName, setFullName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const loadProfile = useCallback(async ({ silent } = {}) => {
    if (!targetUid) {
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'users', targetUid));
      setStoredEmail('');
      setRole('');
      setAvatarUrl('');
      setFullName('');
      setFirstName('');
      setLastName('');
      setPhone('');
      setContactEmail('');

      if (snap.exists()) {
        const d = snap.data();
        setStoredEmail(d.email || '');
        setRole(d.role || '');
        setAvatarUrl(d.avatarUrl || d.photoURL || '');
        setFullName(d.fullName || d.displayName || '');
        setFirstName(d.firstName || '');
        setLastName(d.lastName || '');
        setPhone(d.phone || '');
        setContactEmail(d.contactEmail || '');
      }
    } catch (e) {
      console.error('TeamMemberProfileEditor load:', e);
      setError(e.message || 'Could not load profile.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [targetUid]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const displayNameForDoc = () => {
    const fn = optionalString(fullName);
    if (fn) return fn;
    const parts = [optionalString(firstName), optionalString(lastName)].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!targetUid) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const dn = displayNameForDoc();
      const payload = {
        fullName: optionalString(fullName) ? fullName.trim() : deleteField(),
        firstName: optionalString(firstName) ? firstName.trim() : deleteField(),
        lastName: optionalString(lastName) ? lastName.trim() : deleteField(),
        phone: optionalString(phone) ? phone.trim() : deleteField(),
        contactEmail: optionalString(contactEmail) ? contactEmail.trim() : deleteField(),
      };
      if (dn) {
        payload.displayName = dn;
      } else {
        payload.displayName = deleteField();
      }

      await updateDoc(doc(db, 'users', targetUid), payload);
      const actor = auth.currentUser;
      if (auditLog?.companyId && actor) {
        await appendTicketLog(db, {
          companyId: auditLog.companyId,
          ticketId: null,
          action: 'team_profile_updated',
          summary: 'Directory profile fields updated',
          meta: { targetUid },
          actorUid: actor.uid,
          actorEmail: actor.email || '',
          actorRole: auditLog.actorRole || '',
          participantUids: [actor.uid, targetUid].filter(Boolean),
          resourceType: 'team',
        });
      }
      setMessage('Profile saved.');
      await loadProfile({ silent: true });
      onSaved?.();
    } catch (err) {
      console.error('TeamMemberProfileEditor save:', err);
      setError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !targetUid) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setUploadingPhoto(true);
    setError(null);
    setMessage(null);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const safeExt = /^[a-z0-9]+$/i.test(ext) ? ext.toLowerCase() : 'jpg';
      const storageRef = ref(storage, `avatars/${targetUid}/avatar.${safeExt}`);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'users', targetUid), { avatarUrl: url });
      setAvatarUrl(url);
      const actor = auth.currentUser;
      if (auditLog?.companyId && actor) {
        await appendTicketLog(db, {
          companyId: auditLog.companyId,
          ticketId: null,
          action: 'team_profile_photo_updated',
          summary: 'Directory profile photo updated',
          meta: { targetUid },
          actorUid: actor.uid,
          actorEmail: actor.email || '',
          actorRole: auditLog.actorRole || '',
          participantUids: [actor.uid, targetUid].filter(Boolean),
          resourceType: 'team',
        });
      }
      setMessage('Photo updated.');
      onSaved?.();
    } catch (err) {
      console.error('TeamMemberProfileEditor avatar:', err);
      setError(
        err.code === 'storage/unauthorized'
          ? 'Upload blocked by storage rules for this user’s folder.'
          : err.message || 'Could not upload photo.',
      );
    } finally {
      setUploadingPhoto(false);
    }
  };

  const avatarLabel =
    displayNameForDoc() ||
    optionalString(fullName) ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    storedEmail ||
    '?';

  if (!targetUid) {
    return <Alert severity="warning">No user selected.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSave}>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <Box sx={{ position: 'relative', alignSelf: 'center' }}>
            <Avatar src={avatarUrl || undefined} sx={{ width: 88, height: 88, fontSize: 36 }}>
              {avatarLabel.charAt(0).toUpperCase()}
            </Avatar>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                bgcolor: 'background.paper',
                boxShadow: 2,
              }}
              disabled={uploadingPhoto}
              component="label"
              aria-label="Upload profile photo"
            >
              {uploadingPhoto ? <CircularProgress size={20} /> : <PhotoCameraIcon fontSize="small" />}
              <input type="file" accept="image/*" hidden onChange={handleAvatarChange} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {role && (
              <Chip
                size="small"
                label={String(role).replace(/_/g, ' ')}
                sx={{ textTransform: 'capitalize', mb: 1 }}
              />
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              Login email (read-only)
            </Typography>
            <Typography variant="body2">{storedEmail || '—'}</Typography>
          </Box>
        </Stack>

        <Divider />

        <TextField
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          fullWidth
          size="small"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth size="small" />
          <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth size="small" />
        </Stack>
        <TextField
          label="Contact email"
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          fullWidth
          size="small"
          helperText="Does not change login email."
        />
        <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth size="small" />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button type="button" variant="outlined" onClick={() => loadProfile()} disabled={saving}>
            Reset
          </Button>
          <Button type="submit" variant="contained" disabled={saving || uploadingPhoto}>
            {saving ? 'Saving…' : 'Save profile'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
